<template>
  <div class="page-wrap">

    <!-- ── Page Header ── -->
    <div class="page-header">
      <div>
        <h1 class="page-title">Carts List</h1>
        <span class="page-subtitle">Manage and track all shopping carts</span>
      </div>
    </div>

    <!-- ── Filters ── -->
    <div class="filter-panel">
      <div class="filter-panel__row">

        <div class="filter-field filter-field--grow">
          <label class="filter-label">Search</label>
          <div class="input-icon-wrap">
            <i class="fa fa-search input-icon"></i>
            <input
                placeholder="Search by user ID, name or cart ID..."
                v-model="inputFilters.q"
                class="dd-input dd-input--icon"
                v-on:keyup.enter="filter"
            />
          </div>
        </div>

        <div class="filter-field">
          <label class="filter-label">Last Update Date</label>
          <date-picker
              v-model="inputFilters.date"
              locale="en"
              format="YYYY-MM-DD"
              range
              clearable
              color="#f42f4b"
              class="dd-datepicker"
          />
        </div>

        <div class="filter-field filter-field--sm">
          <label class="filter-label">Cart Status</label>
          <select v-model="inputFilters.status" class="dd-input">
            <option value="">All</option>
            <option value="1">Open</option>
            <option value="0">Purchased</option>
            <option value="2">In Process</option>
          </select>
        </div>

        <div class="filter-field filter-field--sm">
          <label class="filter-label">Buyer Type</label>
          <select v-model="inputFilters.buyer_type" class="dd-input">
            <option value="">All</option>
            <option value="customer">Customer</option>
            <option value="admin">Admin</option>
          </select>
        </div>

        <div class="filter-field filter-field--action">
          <label class="filter-label" style="visibility:hidden">Go</label>
          <button class="btn-filter" @click="filter">
            <i class="fa fa-search"></i> Filter
          </button>
        </div>

      </div>
    </div>

    <!-- ── Table ── -->
    <div class="table-panel">
      <div class="table-responsive-wrap">
        <table class="dd-table">
          <thead>
          <tr>
            <th>Cart ID</th>
            <th>Customer</th>
            <th>Items</th>
            <th>Payable</th>
            <th>Last Update</th>
            <th>Buyer</th>
            <th>Last Action</th>
            <th>Status</th>
          </tr>
          </thead>
          <tbody>
          <router-link
              tag="tr"
              :to="{ name: 'cart.show', params: { id: cart.id } }"
              v-for="cart in items"
              :key="cart.id"
              class="dd-table__row"
          >
            <td>
              <span class="order-id">#{{ cart.id }}</span>
            </td>
            <td>
              <div class="customer-info">
                <div class="customer-avatar">
                  {{ getInitial(cart.user) }}
                </div>
                <div>
                  <div class="customer-name">
                    {{ cart.user.first_name }} {{ cart.user.last_name }}
                  </div>
                  <div class="customer-id">ID: {{ cart.user.id }}</div>
                </div>
              </div>
            </td>
            <td>
              <span class="item-count">{{ cart.items.length }}</span>
              <span class="item-count-label"> item{{ cart.items.length !== 1 ? 's' : '' }}</span>
            </td>
            <td>
                <span class="payable" v-if="cart.meta">
                  AED {{ cart.meta['payable'] }}
                </span>
              <span class="text-muted" v-else>—</span>
            </td>
            <td class="date-cell">{{ formatDate(cart.last_update) || '—' }}</td>
            <td>
                <span class="buyer-badge" v-if="cart.meta && cart.meta.buyer_type">
                  <i :class="cart.meta.buyer_type === 'admin' ? 'fa fa-shield' : 'fa fa-user'"></i>
                  {{ cart.meta.buyer_type }}
                </span>
              <span class="text-muted" v-else>—</span>
            </td>
            <td>
              <span class="action-log">{{ cart.last_action || '—' }}</span>
            </td>
            <td v-html="getStatusBadge(cart.status)"></td>
          </router-link>

          <!-- Empty state -->
          <tr v-if="items && items.length === 0">
            <td colspan="8" class="empty-state">
              <i class="fa fa-shopping-cart"></i>
              <div>No carts found</div>
            </td>
          </tr>
          </tbody>
        </table>
      </div>

      <!-- ── Pagination ── -->
      <div class="pagination-wrap">
        <div class="pagination-info">
          Showing {{ items ? items.length : 0 }} of {{ totalItems }} carts
        </div>
        <b-pagination
            v-model="page"
            :total-rows="totalItems"
            :per-page="perPage"
            last-number
            class="dd-pagination"
        ></b-pagination>
      </div>
    </div>

  </div>
</template>

<script>
import { config } from "../helpers";

export default {
  name: "Carts",
  data() {
    return {
      url: "",
      items: [],
      inputFilters: {
        q: null,
        status: "",
        buyer_type: "",
        date: [],
      },
      isFiltered: false,
      filters: {},
      page: 1,
      lastPage: 1,
      totalItems: 1,
      perPage: 1,
    };
  },

  mounted() {
    this.url = config("BACKEND_ROOT") + "/admin/carts";
    this.loadData();
  },

  methods: {
    loadData() {
      let myData = { page: this.page, sort_by: "updated_at" };
      if (this.isFiltered) {
        myData = {
          ...myData,
          q:          this.filters.q,
          date:       this.filters.date,
          status:     this.filters.status,
          buyer_type: this.filters.buyer_type,
        };
      }
      axios.get(this.url, { params: myData }).then((response) => {
        this.items      = response.data.data;
        this.page       = response.data.meta.current_page;
        this.lastPage   = response.data.meta.last_page;
        this.totalItems = response.data.meta.total;
        this.perPage    = response.data.meta.per_page;
      });
    },

    filter() {
      this.page       = 1;
      this.isFiltered = true;
      const filters   = {};

      if (this.inputFilters.q)           filters.q          = this.inputFilters.q;
      if (this.inputFilters.status)      filters.status     = this.inputFilters.status;
      if (this.inputFilters.buyer_type)  filters.buyer_type = this.inputFilters.buyer_type;
      if (this.inputFilters.date[0]) {
        filters.date = {
          start: this.inputFilters.date[0],
          end:   this.inputFilters.date[1],
        };
      }

      this.filters = filters;
      this.loadData();
    },

    formatDate(date) {
      if (!date) return null;
      const d = new Date(date);
      return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()} ${d.getHours()}:${String(d.getMinutes()).padStart(2, "0")}`;
    },

    getInitial(user) {
      return (user?.first_name || user?.email || "?")[0].toUpperCase();
    },

    getStatusBadge(status) {
      // status: 0 = Purchased, 1 = Open, 2 = In Process
      const map = {
        0: ['status-badge status-badge--green', 'Purchased'],
        1: ['status-badge status-badge--amber', 'Open'],
        2: ['status-badge status-badge--blue',  'In Process'],
      };
      const [cls, label] = map[status] ?? ['status-badge status-badge--gray', 'Unknown'];
      return `<span class="${cls}">${label}</span>`;
    },
  },

  watch: {
    page() { this.loadData(); },
  },
};
</script>

<style scoped>
/* ══════════════════════════════════════
   Page layout
══════════════════════════════════════ */
.page-wrap {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.page-header { display: flex; align-items: flex-end; justify-content: space-between; flex-wrap: wrap; gap: 12px; }
.page-title  { font-size: 20px; font-weight: 500; color: var(--text-1); margin: 0 0 2px; }
.page-subtitle { font-size: 12px; color: var(--text-3); }

/* ══════════════════════════════════════
   Filter panel
══════════════════════════════════════ */
.filter-panel {
  background: var(--bg-panel);
  border: 1px solid var(--border);
  border-radius: 12px;
  padding: 1.1rem 1.25rem;
}
.filter-panel__row {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: flex-end;
}
.filter-field         { display: flex; flex-direction: column; min-width: 160px; }
.filter-field--grow   { flex: 1; }
.filter-field--sm     { min-width: 130px; max-width: 160px; }
.filter-field--action { min-width: auto; }

.filter-label {
  font-size: 11px;
  font-weight: 500;
  color: var(--text-3);
  text-transform: uppercase;
  letter-spacing: .4px;
  margin-bottom: 5px;
}

.dd-input {
  height: 36px;
  background: var(--bg-base);
  border: 1px solid var(--border);
  border-radius: 8px;
  color: var(--text-1);
  font-size: 13px;
  padding: 0 10px;
  outline: none;
  width: 100%;
  transition: border-color .15s;
}
.dd-input:focus         { border-color: var(--accent); }
.dd-input::placeholder  { color: var(--text-3); }

.input-icon-wrap { position: relative; }
.input-icon {
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-3);
  font-size: 13px;
}
.dd-input--icon { padding-left: 32px; }

.btn-filter {
  height: 36px;
  padding: 0 16px;
  background: var(--accent);
  border: none;
  border-radius: 8px;
  color: #fff;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: opacity .15s;
}
.btn-filter:hover { opacity: .88; }

/* ══════════════════════════════════════
   Table panel
══════════════════════════════════════ */
.table-panel {
  background: var(--bg-panel);
  border: 1px solid var(--border);
  border-radius: 12px;
  overflow: hidden;
}
.table-responsive-wrap { overflow-x: auto; }

.dd-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}
.dd-table thead tr  { border-bottom: 1px solid var(--border); }
.dd-table th {
  padding: 10px 14px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: .5px;
  color: var(--text-3);
  white-space: nowrap;
  text-align: left;
}
.dd-table td {
  padding: 12px 14px;
  color: var(--text-2);
  border-bottom: 1px solid var(--border);
  vertical-align: middle;
}
.dd-table__row            { cursor: pointer; transition: background .12s; }
.dd-table__row:hover td   { background: var(--bg-hover); }
.dd-table__row:last-child td { border-bottom: none; }

/* Cells */
.order-id { font-weight: 500; color: var(--text-1); }

.customer-info   { display: flex; align-items: center; gap: 10px; }
.customer-avatar {
  width: 30px; height: 30px;
  background: rgba(244,47,75,.12);
  color: var(--accent);
  border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  font-size: 12px;
  font-weight: 600;
  flex-shrink: 0;
}
.customer-name { font-size: 13px; font-weight: 500; color: var(--text-1); }
.customer-id   { font-size: 11px; color: var(--text-3); }

.item-count       { font-weight: 500; color: var(--text-1); }
.item-count-label { font-size: 12px; color: var(--text-3); }

.payable    { font-weight: 500; color: var(--text-1); }
.text-muted { color: var(--text-3); }

.date-cell  { font-size: 12px; color: var(--text-3); white-space: nowrap; }

.buyer-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 9px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 500;
  background: var(--bg-hover);
  border: 1px solid var(--border);
  color: var(--text-2);
  text-transform: capitalize;
}

.action-log {
  font-size: 12px;
  color: var(--text-3);
  max-width: 160px;
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Status badges */
.status-badge {
  display: inline-flex;
  align-items: center;
  padding: 3px 9px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 500;
  white-space: nowrap;
}
.status-badge--green { background: rgba(34,197,94,.12);   color: #22c55e; }
.status-badge--amber { background: rgba(245,158,11,.12);  color: #f59e0b; }
.status-badge--blue  { background: rgba(129,140,248,.12); color: #818cf8; }
.status-badge--gray  { background: rgba(156,163,175,.12); color: #9ca3af; }

/* Empty state */
.empty-state {
  text-align: center;
  padding: 3rem 1rem !important;
  color: var(--text-3);
}
.empty-state i { font-size: 32px; display: block; margin-bottom: 8px; }

/* ══════════════════════════════════════
   Pagination
══════════════════════════════════════ */
.pagination-wrap {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: .75rem 1.25rem;
  border-top: 1px solid var(--border);
  flex-wrap: wrap;
  gap: 8px;
}
.pagination-info { font-size: 12px; color: var(--text-3); }

.dd-pagination >>> .page-link {
  background: var(--bg-hover);
  border-color: var(--border);
  color: var(--text-2);
  font-size: 13px;
}
.dd-pagination >>> .page-item.active .page-link {
  background: var(--accent);
  border-color: var(--accent);
  color: #fff;
}
.dd-pagination >>> .page-link:hover {
  background: var(--bg-hover);
  border-color: var(--accent);
  color: var(--accent);
}

/* ══════════════════════════════════════
   Responsive
══════════════════════════════════════ */
@media (max-width: 768px) {
  .filter-panel__row  { flex-direction: column; }
  .filter-field,
  .filter-field--sm,
  .filter-field--grow { width: 100%; max-width: 100%; }
}
</style>